import { cn } from '@foundation/utils';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { MapMarkerPin } from './MapMarkerPin';

export type MapLocation = {
  readonly id: string;
  readonly name: string;
  readonly latitude: number;
  readonly longitude: number;
};

export type MapRef = {
  readonly panTo: (position: { lat: number; lng: number }) => void;
  readonly moveCamera: (options: google.maps.CameraOptions) => void;
  readonly resetCamera: () => void;
};

type MapProps = {
  readonly locations: MapLocation[];
  readonly selected?: string | null;
  readonly googleMapsApiKey: string;
  readonly onMarkerClick?: (
    locationId: string,
    lat: number,
    lng: number,
  ) => void;
  readonly className?: string;
  readonly defaultCenter?: {
    readonly lat: number;
    readonly lng: number;
  };
  readonly defaultZoom?: number;
  readonly maxAutoZoom?: number;
  readonly markerIcon?: {
    readonly selected: {
      readonly fillColor: string;
      readonly scale: number;
    };
    readonly unselected: {
      readonly fillColor: string;
      readonly scale: number;
    };
  };
  readonly loadingElement?: React.ReactNode;
  readonly showAllButton?: boolean;
  readonly showAllLabel?: string;
  readonly onShowAll?: () => void;
};

const defaultMapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultMapCenter = {
  lat: 49.8153,
  lng: 6.1296,
};

const defaultMarkerIcon = {
  selected: {
    fillColor: '#cd0039',
    scale: 1.8,
  },
  unselected: {
    fillColor: '#ff5252',
    scale: 1.5,
  },
};

const MapComponent = forwardRef<MapRef, MapProps>(
  (
    {
      locations,
      selected,
      googleMapsApiKey,
      onMarkerClick,
      className = '',
      defaultCenter = defaultMapCenter,
      defaultZoom = 10,
      maxAutoZoom,
      markerIcon = defaultMarkerIcon,
      loadingElement = null,
      showAllButton = false,
      showAllLabel = 'Show all',
      onShowAll,
    },
    ref,
  ) => {
    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey,
      version: 'weekly',
      libraries: ['marker'],
    });

    const mapRef = useRef<google.maps.Map | null>(null);
    const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
    const hasInitializedRef = useRef(false);
    const initialZoomRef = useRef<number | null>(null);
    const initialCenterRef = useRef<google.maps.LatLng | null>(null);

    const resetCamera = () => {
      if (
        mapRef.current &&
        initialZoomRef.current &&
        initialCenterRef.current
      ) {
        mapRef.current.moveCamera({
          zoom: initialZoomRef.current,
          center: initialCenterRef.current,
        });
      }
    };

    useImperativeHandle(ref, () => ({
      panTo: (position: { lat: number; lng: number }) => {
        if (mapRef.current) {
          mapRef.current.panTo(position);
        }
      },
      moveCamera: (options: google.maps.CameraOptions) => {
        if (mapRef.current) {
          mapRef.current.moveCamera(options);
        }
      },
      resetCamera,
    }));

    const placeMarkers = useCallback(
      (map: google.maps.Map, currentLocations: MapLocation[]) => {
        // Clear existing markers
        markersRef.current.forEach((marker) => {
          marker.map = null;
        });
        markersRef.current = [];

        // Create markers
        currentLocations.forEach((location) => {
          if (!location.latitude || !location.longitude) return;

          const isSelected = selected === location.id;
          const iconConfig = isSelected
            ? markerIcon.selected
            : markerIcon.unselected;

          const pinElement = document.createElement('div');
          pinElement.innerHTML = MapMarkerPin({
            fillColor: iconConfig.fillColor,
            scale: iconConfig.scale,
          });
          pinElement.style.cursor = 'pointer';

          const marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: { lat: location.latitude, lng: location.longitude },
            content: pinElement,
            title: location.name,
          });
          marker.id = location.id;
          marker.addListener('gmp-click', () => {
            onMarkerClick?.(location.id, location.latitude, location.longitude);
          });

          markersRef.current.push(marker);
        });

        // Fit bounds only on initial load
        if (!hasInitializedRef.current && currentLocations.length > 0) {
          const bounds = new google.maps.LatLngBounds();
          currentLocations.forEach((location) => {
            if (!location.latitude || !location.longitude) return;
            bounds.extend({ lat: location.latitude, lng: location.longitude });
          });
          map.fitBounds(bounds);
          hasInitializedRef.current = true;

          // Save zoom/center AFTER fitBounds settles
          google.maps.event.addListenerOnce(map, 'idle', () => {
            if (maxAutoZoom !== undefined) {
              const autoZoom = map.getZoom();
              if (
                autoZoom !== undefined &&
                autoZoom !== null &&
                autoZoom > maxAutoZoom
              ) {
                map.setZoom(maxAutoZoom);
              }
            }

            initialZoomRef.current = map.getZoom() ?? null;
            initialCenterRef.current = map.getCenter() ?? null;
          });
        } else if (hasInitializedRef.current && !selected) {
          // Only reset zoom/center when there is no active selection.
          // If something is selected, the parent controls the camera via moveCamera().

          if (initialZoomRef.current !== null) {
            map.setZoom(initialZoomRef.current);
          }
          if (initialCenterRef.current !== null) {
            map.setCenter(initialCenterRef.current);
          }
        }
      },
      [selected, markerIcon, onMarkerClick, maxAutoZoom],
    );

    const onLoad = (map: google.maps.Map) => {
      mapRef.current = map;
      placeMarkers(map, locations);
    };

    // Re-place markers when locations change
    useEffect(() => {
      if (!mapRef.current) return;
      placeMarkers(mapRef.current, locations);
    }, [locations, placeMarkers]);

    // Update marker colors when selected changes
    useEffect(() => {
      if (!mapRef.current) return;

      if (mapRef.current && markersRef.current.length > 0) {
        markersRef.current.forEach((marker) => {
          const location = locations.find((loc) => loc.id === marker.id);
          if (!location) {
            return;
          }
          const isSelected = selected === location.id;
          const iconConfig = isSelected
            ? markerIcon.selected
            : markerIcon.unselected;

          // Update marker content with new color
          const pinElement = document.createElement('div');
          pinElement.innerHTML = MapMarkerPin({
            fillColor: iconConfig.fillColor,
            scale: iconConfig.scale,
          });
          pinElement.style.cursor = 'pointer';
          marker.content = pinElement;
        });
      }
    }, [selected, locations, markerIcon]);

    return (
      <div className={cn(className, 'relative')}>
        {!isLoaded ? (
          <div className="flex justify-center items-center mt-5">
            {loadingElement}
          </div>
        ) : (
          <>
            <GoogleMap
              mapContainerStyle={defaultMapContainerStyle}
              center={defaultCenter}
              zoom={defaultZoom}
              onLoad={onLoad}
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
                mapId: 'DEMO_MAP_ID', // Required for AdvancedMarkerElement
              }}
            />
            {showAllButton && locations.length > 0 && (
              <button
                type="button"
                onClick={onShowAll}
                aria-label={showAllLabel}
                className="absolute top-3 right-3 z-10 min-h-12 text-sm font-medium text-gray-900  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
              >
                <span className="rounded-md bg-white px-3 py-1 shadow-md">
                  {showAllLabel}
                </span>
              </button>
            )}
          </>
        )}
      </div>
    );
  },
);

MapComponent.displayName = 'Map';

export { MapComponent as Map };
